import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Save, X, Plus, ShieldCheck, FileText } from "lucide-react";
import "@/BrandPersonalityEditor.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STYLE_PRESETS = [
  "Friendly & Conversational",
  "Premium & Polished",
  "Direct & Confident",
  "Warm & Family-focused",
  "Modern & Minimal",
  "Boutique & Personal",
];

export default function BrandPersonalityEditor({ branch, onSaved, onClose }) {
  const [agencyStyle, setAgencyStyle] = useState(branch.agency_style || "Friendly & Conversational");
  const [brandVoice, setBrandVoice] = useState(branch.brand_voice || "");
  const [targetAudience, setTargetAudience] = useState(branch.target_audience || "");
  const [doRules, setDoRules] = useState(branch.do_rules || []);
  const [avoidRules, setAvoidRules] = useState(branch.avoid_rules || []);
  const [newDo, setNewDo] = useState("");
  const [newAvoid, setNewAvoid] = useState("");
  const [pdIncludeBullets, setPdIncludeBullets] = useState(branch.pd_include_bullets !== false);
  const [pdBulletsPosition, setPdBulletsPosition] = useState(branch.pd_bullets_position || "top");
  const [pdWordTarget, setPdWordTarget] = useState(branch.pd_word_target || 220);
  const [saving, setSaving] = useState(false);

  // Re-hydrate when the branch selection changes mid-edit
  useEffect(() => {
    setAgencyStyle(branch.agency_style || "Friendly & Conversational");
    setBrandVoice(branch.brand_voice || "");
    setTargetAudience(branch.target_audience || "");
    setDoRules(branch.do_rules || []);
    setAvoidRules(branch.avoid_rules || []);
    setPdIncludeBullets(branch.pd_include_bullets !== false);
    setPdBulletsPosition(branch.pd_bullets_position || "top");
    setPdWordTarget(branch.pd_word_target || 220);
  }, [branch.id]);

  const addDo = () => {
    const t = newDo.trim();
    if (!t || doRules.includes(t)) return;
    setDoRules([...doRules, t]);
    setNewDo("");
  };
  const addAvoid = () => {
    const t = newAvoid.trim();
    if (!t || avoidRules.includes(t)) return;
    setAvoidRules([...avoidRules, t]);
    setNewAvoid("");
  };
  const removeDo = (rule) => setDoRules(doRules.filter((r) => r !== rule));
  const removeAvoid = (rule) => setAvoidRules(avoidRules.filter((r) => r !== rule));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        agency_style: agencyStyle,
        brand_voice: brandVoice.trim(),
        target_audience: targetAudience.trim(),
        do_rules: doRules,
        avoid_rules: avoidRules,
        pd_include_bullets: pdIncludeBullets,
        pd_bullets_position: pdBulletsPosition,
        pd_word_target: Number(pdWordTarget) || 220,
      };
      await axios.patch(`${API}/admin/branches/${branch.id}`, payload);
      const updated = { ...branch, ...payload };
      onSaved && onSaved(updated);
      toast.success("Brand personality saved — all AI tools now use these settings");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save brand personality");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bpe-container" data-testid="brand-personality-editor">
      <div className="bpe-head">
        <div className="bpe-title">
          <Sparkles size={20} />
          <div>
            <h2>Brand Personality</h2>
            <p>Used by every AI tool in this branch — Scripts, Valuation, Property Description, Tours, Derek.</p>
          </div>
        </div>
        <button className="bpe-close" onClick={onClose} data-testid="bpe-close"><X size={18} /></button>
      </div>

      <div className="bpe-grid">
        <section className="bpe-section">
          <h3>Tone preset</h3>
          <p className="bpe-section-hint">A starting voice — refined by the free-text fields below.</p>
          <div className="bpe-presets">
            {STYLE_PRESETS.map((s) => (
              <button
                type="button"
                key={s}
                className={`bpe-preset ${agencyStyle === s ? "is-active" : ""}`}
                onClick={() => setAgencyStyle(s)}
                data-testid={`bpe-preset-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        </section>

        <section className="bpe-section">
          <h3>Brand voice</h3>
          <p className="bpe-section-hint">A short paragraph capturing how this brand talks. Used in every AI prompt.</p>
          <textarea
            className="bpe-textarea"
            rows={3}
            placeholder="e.g. We're the warm, knowledgeable local agent who treats every house like our own. Confident but never salesy."
            value={brandVoice}
            onChange={(e) => setBrandVoice(e.target.value)}
            data-testid="bpe-brand-voice"
          />
        </section>

        <section className="bpe-section">
          <h3>Target audience</h3>
          <p className="bpe-section-hint">Who is the typical buyer in your patch? Helps the AI pitch language correctly.</p>
          <input
            type="text"
            className="bpe-input"
            placeholder="e.g. Young families upsizing from London; second-time movers in SW20"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            data-testid="bpe-target-audience"
          />
        </section>

        <section className="bpe-section bpe-rules">
          <h3>Always include / never include</h3>
          <p className="bpe-section-hint">Hard rules every AI tool will follow. Add as many as you like.</p>

          <div className="bpe-rules-grid">
            <div className="bpe-rule-col bpe-rule-do">
              <div className="bpe-rule-head"><ShieldCheck size={14} /> Always</div>
              <ul className="bpe-rule-list">
                {doRules.map((r) => (
                  <li key={r}>
                    <span>{r}</span>
                    <button type="button" onClick={() => removeDo(r)} aria-label="Remove"><X size={11} /></button>
                  </li>
                ))}
                {doRules.length === 0 && <li className="bpe-rule-empty">No always-rules yet.</li>}
              </ul>
              <div className="bpe-rule-add">
                <input
                  type="text"
                  placeholder="e.g. Always use British spellings"
                  value={newDo}
                  onChange={(e) => setNewDo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDo(); } }}
                  data-testid="bpe-new-do"
                />
                <button type="button" onClick={addDo} disabled={!newDo.trim()} data-testid="bpe-add-do">
                  <Plus size={14} />
                </button>
              </div>
            </div>

            <div className="bpe-rule-col bpe-rule-avoid">
              <div className="bpe-rule-head"><X size={14} /> Never</div>
              <ul className="bpe-rule-list">
                {avoidRules.map((r) => (
                  <li key={r}>
                    <span>{r}</span>
                    <button type="button" onClick={() => removeAvoid(r)} aria-label="Remove"><X size={11} /></button>
                  </li>
                ))}
                {avoidRules.length === 0 && <li className="bpe-rule-empty">No avoid-rules yet.</li>}
              </ul>
              <div className="bpe-rule-add">
                <input
                  type="text"
                  placeholder="e.g. Don't mention parking unless guaranteed"
                  value={newAvoid}
                  onChange={(e) => setNewAvoid(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAvoid(); } }}
                  data-testid="bpe-new-avoid"
                />
                <button type="button" onClick={addAvoid} disabled={!newAvoid.trim()} data-testid="bpe-add-avoid">
                  <Plus size={14} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="bpe-section bpe-pd">
          <h3><FileText size={14} style={{ verticalAlign: "middle", marginRight: 4 }} /> Property Description preferences</h3>
          <p className="bpe-section-hint">Defaults the Property Description tool will use for this branch.</p>

          <div className="bpe-pd-grid">
            <label className="bpe-pd-toggle">
              <input
                type="checkbox"
                checked={pdIncludeBullets}
                onChange={(e) => setPdIncludeBullets(e.target.checked)}
                data-testid="bpe-pd-include-bullets"
              />
              <span>Include feature bullets</span>
            </label>

            <div className="bpe-pd-field">
              <label className="bpe-pd-label">Bullets position</label>
              <div className="bpe-pd-pills">
                {["top", "bottom", "none"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`bpe-pd-pill ${pdBulletsPosition === p ? "is-active" : ""}`}
                    onClick={() => setPdBulletsPosition(p)}
                    disabled={!pdIncludeBullets && p !== "none"}
                    data-testid={`bpe-pd-bullets-${p}`}
                  >
                    {p[0].toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bpe-pd-field">
              <label className="bpe-pd-label">Target word count</label>
              <input
                type="number"
                min={80}
                max={500}
                value={pdWordTarget}
                onChange={(e) => setPdWordTarget(e.target.value)}
                className="bpe-input bpe-input-sm"
                data-testid="bpe-pd-word-target"
              />
              <span className="bpe-pd-hint">Rightmove sweet spot: 200–250</span>
            </div>
          </div>
        </section>
      </div>

      <div className="bpe-footer">
        <button className="bpe-save" onClick={handleSave} disabled={saving} data-testid="bpe-save">
          <Save size={15} /> {saving ? "Saving…" : "Save personality"}
        </button>
      </div>
    </div>
  );
}
